import { useState, type FormEvent } from "react"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowRight, Layers3 } from "lucide-react"
import { toast } from "sonner"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { AuthLayout, AuthSwitch } from "@/components/auth/auth-layout"
import { describeAuthError } from "@/lib/auth-errors"
import { Field } from "@/components/workspace/primitives"
import { authClient } from "@/lib/auth-client"
import { useQuery } from "@tanstack/react-query"
import { configQuery } from "@/lib/queries"

export const Route = createFileRoute("/sign-in")({
  validateSearch: z.object({ redirect: z.string().optional() }),
  component: SignIn,
})

function SignIn() {
  const navigate = useNavigate()
  const { redirect } = Route.useSearch()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [remember, setRemember] = useState(true)
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)
  // Portal mode: the staff workspace is the only door; Vienna's own password form stays out of the way.
  const portalUrl = useQuery(configQuery).data?.portalUrl ?? null
  const ssoError = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("error") === "portal"

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError("")
    setBusy(true)
    const result = await authClient.signIn.email({ email, password, rememberMe: remember })
    setBusy(false)
    if (result.error) {
      setError(describeAuthError(result.error))
      return
    }
    toast.success(`Welcome back${result.data?.user.name ? `, ${result.data.user.name.split(" ")[0]}` : ""}`)
    await navigate({ to: redirect && redirect.startsWith("/") ? redirect : "/" })
  }

  if (portalUrl) {
    return (
      <AuthLayout mark={<Layers3 size={23} />} title="Open Vienna from the team workspace" subtitle="Your Dr. Shumard staff account signs you in here; there is no separate Vienna password.">
        {ssoError && (
          <p className="text-sm text-destructive" role="alert">
            That sign-in link has expired or was already used. Open Vienna again from the workspace.
          </p>
        )}
        <Button className="w-full" size="lg" asChild>
          <a href={`${portalUrl.replace(/\/$/, "")}/vienna`}>
            Go to the team workspace
            <ArrowRight />
          </a>
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      mark={<Layers3 size={23} />}
      title="Welcome back"
      subtitle="Sign in to your Vienna workspace."
      footer={
        <AuthSwitch prompt="New to Vienna?">
          <Button variant="link" asChild>
            <Link to="/create-workspace">Create a workspace</Link>
          </Button>
        </AuthSwitch>
      }
    >
      <form onSubmit={submit} className="ws-auth-fields">
        <Field label="Work email">
          {(id) => <Input id={id} type="email" value={email} required autoComplete="email" autoFocus onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Field label="Password">
          {(id) => <Input id={id} type="password" value={password} required autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} />}
        </Field>
        <div className="ws-auth-options">
          <label>
            <Checkbox checked={remember} onCheckedChange={(v) => setRemember(v === true)} />
            Keep me signed in
          </label>
          <Button type="button" variant="link" asChild>
            <Link to="/forgot-password">Forgot password?</Link>
          </Button>
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <Button className="w-full" size="lg" type="submit" disabled={busy}>
          Sign in
          <ArrowRight />
        </Button>
      </form>
    </AuthLayout>
  )
}
